import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Alert, Button, Checkbox, FormGroup, HelperText, HelperTextItem, Modal, ModalVariant, Stack, StackItem, Tab, Tabs, TabTitleText, TextArea, TextInput } from '@patternfly/react-core';
import { CheckCircleIcon, TimesCircleIcon } from '@patternfly/react-icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { composeRegex, decomposeRegex, EMPTY_REGEX, ensureInlineFlag, extractFlagGroup, removeInlineFlag } from '../util/regex-composer';
import RegexHelp from './regex-help';
import { appConfigSelectors } from '../store/appConfig/appConfigSlice';
import { DisabledFeature, isFeatureDisabled } from '../store/appConfig/appDeploymentInfoTypes';
import { assistantSelectors, generateRegexStarted, resetRegexAssistant } from '../store/assistant/assistantSlice';

import './regex-composer-modal.css';

interface RegexComposerModalProps {
    isOpen: boolean;
    currentRegex: string;
    onApply: (regex: string) => void;
    onClose: () => void;
    includePlaceholder?: string;
    excludePlaceholder?: string;
    /** When false, the Compose tab is hidden and only Advanced + Test tabs are shown. Default true. */
    showComposerTab?: boolean;
}

type TabKey = 'composer' | 'advanced' | 'test' | 'assistant';

const RegexComposerModal = (props: RegexComposerModalProps) => {
    const { isOpen, currentRegex, onApply, onClose, includePlaceholder, excludePlaceholder, showComposerTab = true } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const deploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);
    const showAssistantTab = deploymentInfo ? !isFeatureDisabled(deploymentInfo, DisabledFeature.RegexAssistant) : false;

    const isRegexGenerating = useSelector(assistantSelectors.selectIsRegexGenerating);
    const regexError = useSelector(assistantSelectors.selectRegexError);
    const regexResult = useSelector(assistantSelectors.selectRegexResult);

    const [assistantDescription, setAssistantDescription] = useState('');
    const [assistantMatchExamples, setAssistantMatchExamples] = useState('');
    const [assistantNonMatchExamples, setAssistantNonMatchExamples] = useState('');
    const [isAssistantCaseInsensitive, setIsAssistantCaseInsensitive] = useState(true);

    const [includeText, setIncludeText] = useState('');
    const [excludeText, setExcludeText] = useState('');
    const [caseInsensitive, setCaseInsensitive] = useState(false);
    const [includeWildcards, setIncludeWildcards] = useState(true);
    const [excludeWildcards, setExcludeWildcards] = useState(true);
    const [composerAvailable, setComposerAvailable] = useState(true);
    const [activeTab, setActiveTab] = useState<TabKey>('composer');
    const [advancedText, setAdvancedText] = useState('');
    const [testInput, setTestInput] = useState('');
    const [testRegex, setTestRegex] = useState('');
    const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

    // Populate advancedText when regex assistant succeeds or case sensitivity is toggled
    const lastRegexResultRef = useRef<string | null>(null);
    useEffect(() => {
        if (regexResult) {
            if (regexResult !== lastRegexResultRef.current) {
                // New result arrived: apply flag to the raw result
                lastRegexResultRef.current = regexResult;
                let result = regexResult;
                if (isAssistantCaseInsensitive) {
                    result = ensureInlineFlag(result, 'i');
                } else {
                    result = removeInlineFlag(result, 'i');
                }
                setAdvancedText(result);
            } else {
                // Same result, checkbox toggled: apply flag to current advancedText
                setAdvancedText(prev => isAssistantCaseInsensitive
                    ? ensureInlineFlag(prev, 'i')
                    : removeInlineFlag(prev, 'i')
                );
            }
        }
    }, [regexResult, isAssistantCaseInsensitive]);

    // Reset assistant state when modal closes
    useEffect(() => {
        if (!isOpen) {
            dispatch(resetRegexAssistant());
            setAssistantDescription('');
            setAssistantMatchExamples('');
            setAssistantNonMatchExamples('');
            setIsAssistantCaseInsensitive(true);
            lastRegexResultRef.current = null;
        }
    }, [isOpen, dispatch]);

    // Populate fields when opening
    useEffect(() => {
        if (isOpen) {
            dispatch(resetRegexAssistant());
            setTestInput('');
            setTestRegex('');
            setAdvancedText(currentRegex);
            setIsClearConfirmOpen(false);

            if (!showComposerTab) {
                setActiveTab('advanced');
                lastSourceTab.current = 'advanced';
                setIncludeText('');
                setExcludeText('');
                setCaseInsensitive(true);
                setIncludeWildcards(true);
                setExcludeWildcards(true);
                setComposerAvailable(false);
                return;
            }

            const result = decomposeRegex(currentRegex);
            if (result !== null) {
                setActiveTab('composer');
                lastSourceTab.current = 'composer';
                setIncludeText(result.includes.join('\n'));
                setExcludeText(result.excludes.join('\n'));
                setCaseInsensitive(result.caseInsensitive);
                setIncludeWildcards(result.includeWildcards);
                setExcludeWildcards(result.excludeWildcards);
                setComposerAvailable(true);
            } else {
                const tab = currentRegex.length > 0 ? 'advanced' : 'composer';
                setActiveTab(tab);
                lastSourceTab.current = tab;
                setIncludeText('');
                setExcludeText('');
                setCaseInsensitive(true);
                setIncludeWildcards(true);
                setExcludeWildcards(true);
                setComposerAvailable(currentRegex.length === 0);
            }
        }
    }, [isOpen, currentRegex, showComposerTab, dispatch]);

    const includes = useMemo(() => includeText.split('\n').filter(s => s.length > 0), [includeText]);
    const excludes = useMemo(() => excludeText.split('\n').filter(s => s.length > 0), [excludeText]);

    const preview = useMemo(
        () => composeRegex(includes, excludes, caseInsensitive, includeWildcards, excludeWildcards),
        [includes, excludes, caseInsensitive, includeWildcards, excludeWildcards]
    );

    const testResults = useMemo(() => {
        if (testRegex.length === 0) { return null; }
        try {
            let pattern = testRegex;
            let flags = '';
            const pythonFlagWarnings: string[] = [];

            const flagGroupResult = extractFlagGroup(pattern);
            if (flagGroupResult) {
                pattern = flagGroupResult.rest;
                if (flagGroupResult.flags.includes('i')) { flags += 'i'; }
                if (flagGroupResult.flags.includes('m')) { flags += 'm'; }
                if (flagGroupResult.flags.includes('s')) { pythonFlagWarnings.push('(?s) dotall'); }
                if (flagGroupResult.flags.includes('x')) { pythonFlagWarnings.push('(?x) verbose'); }
            }

            const re = new RegExp(pattern, flags);
            const lines = testInput.split('\n').filter(s => s.length > 0);
            return {
                valid: true as const,
                pythonFlagWarnings,
                results: lines.map(line => {
                    const match = line.match(re);
                    return { text: line, matches: match !== null, matchedText: match?.[0] ?? null, matchIndex: match?.index ?? -1 };
                }),
            };
        } catch {
            return { valid: false as const, pythonFlagWarnings: [], results: [] };
        }
    }, [testRegex, testInput]);

    const handleIncludeChange = useCallback((_: unknown, value: string) => {
        setIncludeText(value);
    }, []);

    const handleExcludeChange = useCallback((_: unknown, value: string) => {
        setExcludeText(value);
    }, []);

    const handleCaseInsensitiveChange = useCallback(() => {
        setCaseInsensitive(prev => !prev);
    }, []);

    const handleIncludeWildcardsChange = useCallback(() => {
        setIncludeWildcards(prev => !prev);
    }, []);

    const handleExcludeWildcardsChange = useCallback(() => {
        setExcludeWildcards(prev => !prev);
    }, []);

    const handleAdvancedTextChange = useCallback((_: unknown, value: string) => {
        setAdvancedText(value);
    }, []);

    const handleTestInputChange = useCallback((_: unknown, value: string) => {
        setTestInput(value);
    }, []);

    const handleAssistantDescriptionChange = useCallback((_: unknown, value: string) => {
        setAssistantDescription(value);
    }, []);

    const handleAssistantMatchExamplesChange = useCallback((_: unknown, value: string) => {
        setAssistantMatchExamples(value);
    }, []);

    const handleAssistantNonMatchExamplesChange = useCallback((_: unknown, value: string) => {
        setAssistantNonMatchExamples(value);
    }, []);

    const handleAssistantCaseInsensitiveChange = useCallback(() => {
        setIsAssistantCaseInsensitive(prev => !prev);
    }, []);

    const handleGenerateRegex = useCallback(() => {
        dispatch(generateRegexStarted({
            description: assistantDescription,
            matchExamples: assistantMatchExamples,
            nonMatchExamples: assistantNonMatchExamples,
            requestId: uuidv4(),
        }));
    }, [dispatch, assistantDescription, assistantMatchExamples, assistantNonMatchExamples]);

    const normalizeAdvancedRegex = (text: string) => text.replace(/[\n\r]+/g, '').trim();

    // Keep testRegex in sync while the Test tab is active
    const lastSourceTab = useRef<TabKey>('composer');

    useEffect(() => {
        if (activeTab === 'test') {
            if (lastSourceTab.current === 'composer') {
                setTestRegex(preview);
            } else if (lastSourceTab.current === 'advanced') {
                setTestRegex(normalizeAdvancedRegex(advancedText));
            }
        }
    }, [activeTab, preview, advancedText]);

    const handleTabSelect = useCallback((_: unknown, key: string | number) => {
        const newTab = key as TabKey;
        if (newTab === 'advanced' && activeTab === 'composer') {
            // Sync current composed preview into advanced text only if user typed something
            if (preview !== EMPTY_REGEX) {
                setAdvancedText(preview);
            }
        } else if (newTab === 'composer' && activeTab === 'advanced') {
            // Try to decompose the advanced text back into composer fields
            const result = decomposeRegex(normalizeAdvancedRegex(advancedText));
            if (result !== null) {
                setIncludeText(result.includes.join('\n'));
                setExcludeText(result.excludes.join('\n'));
                setCaseInsensitive(result.caseInsensitive);
                setIncludeWildcards(result.includeWildcards);
                setExcludeWildcards(result.excludeWildcards);
                setComposerAvailable(true);
            } else {
                setIncludeText('');
                setExcludeText('');
                setCaseInsensitive(false);
                setIncludeWildcards(true);
                setExcludeWildcards(true);
                setComposerAvailable(advancedText.length === 0);
            }
        }
        if (newTab !== 'test') {
            // Remember which tab was last active before entering the Test tab
            lastSourceTab.current = newTab === 'assistant' ? 'advanced' : newTab;
        }
        setActiveTab(newTab);
    }, [activeTab, preview, advancedText]);

    const handleApply = useCallback(() => {
        let result: string;
        if (activeTab === 'test') {
            result = testRegex;
        } else if (activeTab === 'advanced' || activeTab === 'assistant') {
            result = normalizeAdvancedRegex(advancedText);
        } else {
            result = preview;
        }
        onApply(result || EMPTY_REGEX);
        onClose();
    }, [activeTab, advancedText, onApply, onClose, preview, testRegex]);

    const handleCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleClearClick = useCallback(() => {
        setIsClearConfirmOpen(true);
    }, []);

    const handleClearCancel = useCallback(() => {
        setIsClearConfirmOpen(false);
    }, []);

    const handleClearConfirm = useCallback(() => {
        setIncludeText('');
        setExcludeText('');
        setCaseInsensitive(false);
        setIncludeWildcards(true);
        setExcludeWildcards(true);
        setAdvancedText('');
        setTestInput('');
        setTestRegex('');
        setComposerAvailable(true);
        setIsClearConfirmOpen(false);
    }, []);

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                variant={ModalVariant.medium}
                title={showComposerTab ? t('regex.composer.title') : t('regex.modal.title')}
                actions={[
                    <Button key="apply" variant="primary" onClick={handleApply} data-testid="regex-composer-apply">
                        {t('regex.composer.apply')}
                    </Button>,
                    <Button key="clear" variant="secondary" onClick={handleClearClick} data-testid="regex-composer-clear">
                        {t('regex.composer.clear')}
                    </Button>,
                    <Button key="cancel" variant="tertiary" onClick={handleCancel}>
                        {t('common.cancel')}
                    </Button>,
                    <RegexHelp key="helpIcon" regexAlwaysOn />,
                ]}
                showClose={false}
                data-testid="regex-composer-modal"
            >
                <Tabs activeKey={activeTab} onSelect={handleTabSelect}>
                    {showComposerTab && (
                        <Tab eventKey="composer" title={<TabTitleText>{t('regex.composer.tabComposer')}</TabTitleText>}>
                            <div className="regex-composer-tab-content" data-testid="regex-composer-tab">
                                <Stack hasGutter>
                                    <StackItem>
                                        <HelperText>
                                            <HelperTextItem>{t('regex.composer.patternNote')}</HelperTextItem>
                                        </HelperText>
                                    </StackItem>
                                    {!composerAvailable && (
                                        <StackItem>
                                            <Alert variant="info" isInline isPlain title={t('regex.composer.cannotDecompose')} />
                                        </StackItem>
                                    )}
                                    <StackItem>
                                        <div className="regex-composer-textareas">
                                            <div>
                                                <FormGroup label={t('regex.composer.includeLabel')} fieldId="regex-composer-include">
                                                    <TextArea
                                                        id="regex-composer-include"
                                                        data-testid="regex-composer-include"
                                                        value={includeText}
                                                        onChange={handleIncludeChange}
                                                        placeholder={includePlaceholder ?? t('regex.composer.includePlaceholder')}
                                                        rows={6}
                                                        resizeOrientation="vertical"
                                                    />
                                                    <HelperText>
                                                        <HelperTextItem>{t('regex.composer.includeHelperText')}</HelperTextItem>
                                                    </HelperText>
                                                </FormGroup>
                                                <Checkbox
                                                    label={t('regex.composer.includeWildcardMatching')}
                                                    isChecked={includeWildcards}
                                                    onChange={handleIncludeWildcardsChange}
                                                    id="regex-composer-include-wildcard-matching"
                                                    data-testid="regex-composer-include-wildcard-matching"
                                                />
                                            </div>
                                            <div>
                                                <FormGroup label={t('regex.composer.excludeLabel')} fieldId="regex-composer-exclude">
                                                    <TextArea
                                                        id="regex-composer-exclude"
                                                        data-testid="regex-composer-exclude"
                                                        value={excludeText}
                                                        onChange={handleExcludeChange}
                                                        placeholder={excludePlaceholder ?? t('regex.composer.excludePlaceholder')}
                                                        rows={6}
                                                        resizeOrientation="vertical"
                                                    />
                                                    <HelperText>
                                                        <HelperTextItem>{t('regex.composer.excludeHelperText')}</HelperTextItem>
                                                    </HelperText>
                                                </FormGroup>
                                                <Checkbox
                                                    label={t('regex.composer.excludeWildcardMatching')}
                                                    isChecked={excludeWildcards}
                                                    onChange={handleExcludeWildcardsChange}
                                                    id="regex-composer-exclude-wildcard-matching"
                                                    data-testid="regex-composer-exclude-wildcard-matching"
                                                />
                                            </div>
                                        </div>
                                    </StackItem>
                                    <StackItem>
                                        <Checkbox
                                            label={t('regex.composer.caseInsensitive')}
                                            isChecked={caseInsensitive}
                                            onChange={handleCaseInsensitiveChange}
                                            id="regex-composer-case-insensitive"
                                            data-testid="regex-composer-case-insensitive"
                                        />
                                    </StackItem>
                                    <StackItem className="regex-composer-preview">
                                        <FormGroup label={t('regex.composer.previewLabel')} fieldId="regex-composer-preview">
                                            <TextInput
                                                id="regex-composer-preview"
                                                data-testid="regex-composer-preview"
                                                value={preview}
                                                readOnly
                                                placeholder={t('regex.composer.previewPlaceholder')}
                                            />
                                        </FormGroup>
                                    </StackItem>
                                </Stack>
                            </div>
                        </Tab>
                    )}
                    <Tab eventKey="advanced" title={<TabTitleText>{t('regex.composer.tabAdvanced')}</TabTitleText>}>
                        <div className="regex-composer-tab-content" data-testid="regex-advanced-tab">
                            <FormGroup label={t('regex.composer.advancedLabel')} fieldId="regex-composer-advanced">
                                <TextArea
                                    id="regex-composer-advanced"
                                    data-testid="regex-composer-advanced"
                                    value={advancedText}
                                    onChange={handleAdvancedTextChange}
                                    rows={4}
                                    resizeOrientation="vertical"
                                    className="regex-composer-advanced-textarea"
                                />
                            </FormGroup>
                        </div>
                    </Tab>
                    {showAssistantTab && (
                        <Tab eventKey="assistant" title={<TabTitleText>{t('assistant.regex.tabTitle')}</TabTitleText>}>
                            <div className="regex-composer-tab-content" data-testid="regex-assistant-tab">
                                <Stack hasGutter>
                                    <StackItem>
                                        <Alert variant="warning" isInline isPlain title={t('assistant.aiWarning')} />
                                    </StackItem>
                                    <StackItem>
                                        <FormGroup label={t('assistant.regex.descriptionLabel')} fieldId="regex-assistant-description">
                                            <TextArea
                                                id="regex-assistant-description"
                                                data-testid="regex-assistant-description"
                                                value={assistantDescription}
                                                onChange={handleAssistantDescriptionChange}
                                                placeholder={t('assistant.regex.descriptionPlaceholder')}
                                                rows={3}
                                                resizeOrientation="vertical"
                                            />
                                            <HelperText>
                                                <HelperTextItem>{t('assistant.regex.descriptionHelperText')}</HelperTextItem>
                                            </HelperText>
                                        </FormGroup>
                                    </StackItem>
                                    <StackItem>
                                        <FormGroup label={t('assistant.regex.matchExamplesLabel')} fieldId="regex-assistant-match-examples">
                                            <TextArea
                                                id="regex-assistant-match-examples"
                                                data-testid="regex-assistant-match-examples"
                                                value={assistantMatchExamples}
                                                onChange={handleAssistantMatchExamplesChange}
                                                placeholder={t('assistant.regex.matchExamplesPlaceholder')}
                                                rows={4}
                                                resizeOrientation="vertical"
                                            />
                                            <HelperText>
                                                <HelperTextItem>{t('assistant.regex.matchExamplesHelperText')}</HelperTextItem>
                                            </HelperText>
                                        </FormGroup>
                                    </StackItem>
                                    <StackItem>
                                        <FormGroup label={t('assistant.regex.nonMatchExamplesLabel')} fieldId="regex-assistant-non-match-examples">
                                            <TextArea
                                                id="regex-assistant-non-match-examples"
                                                data-testid="regex-assistant-non-match-examples"
                                                value={assistantNonMatchExamples}
                                                onChange={handleAssistantNonMatchExamplesChange}
                                                placeholder={t('assistant.regex.nonMatchExamplesPlaceholder')}
                                                rows={4}
                                                resizeOrientation="vertical"
                                            />
                                            <HelperText>
                                                <HelperTextItem>{t('assistant.regex.nonMatchExamplesHelperText')}</HelperTextItem>
                                            </HelperText>
                                        </FormGroup>
                                    </StackItem>
                                    <StackItem>
                                        <Checkbox
                                            label={t('assistant.regex.caseInsensitive')}
                                            isChecked={isAssistantCaseInsensitive}
                                            onChange={handleAssistantCaseInsensitiveChange}
                                            id="regex-assistant-case-insensitive"
                                            data-testid="regex-assistant-case-insensitive"
                                        />
                                    </StackItem>
                                    <StackItem>
                                        <Button
                                            variant="primary"
                                            onClick={handleGenerateRegex}
                                            isLoading={isRegexGenerating}
                                            isDisabled={isRegexGenerating || !assistantDescription.trim()}
                                            data-testid="regex-assistant-generate"
                                        >
                                            {t('assistant.regex.generateButton')}
                                        </Button>
                                    </StackItem>
                                    {regexError && (
                                        <StackItem>
                                            <Alert variant="danger" isInline title={t('assistant.regex.errorTitle')}>
                                                {regexError}
                                            </Alert>
                                        </StackItem>
                                    )}
                                    {regexResult && (
                                        <StackItem>
                                            <Alert variant="success" isInline title={t('assistant.regex.successTitle')}>
                                                <code>{regexResult}</code>
                                            </Alert>
                                        </StackItem>
                                    )}
                                </Stack>
                            </div>
                        </Tab>
                    )}
                    <Tab eventKey="test" title={<TabTitleText>{t('regex.composer.tabTest')}</TabTitleText>}>
                        <div className="regex-composer-tab-content" data-testid="regex-test-tab">
                            <Stack hasGutter>
                                <StackItem className="regex-composer-preview">
                                    <FormGroup label={t('regex.composer.previewLabel')} fieldId="regex-composer-test-pattern">
                                        <TextInput
                                            id="regex-composer-test-pattern"
                                            value={testRegex}
                                            readOnly
                                            placeholder={t('regex.composer.previewPlaceholder')}
                                        />
                                    </FormGroup>
                                </StackItem>
                                <StackItem>
                                    <FormGroup label={t('regex.composer.testInputLabel')} fieldId="regex-composer-test-input">
                                        <TextArea
                                            id="regex-composer-test-input"
                                            data-testid="regex-composer-test-input"
                                            value={testInput}
                                            onChange={handleTestInputChange}
                                            placeholder={t('regex.composer.testInputPlaceholder')}
                                            rows={6}
                                            resizeOrientation="vertical"
                                        />
                                    </FormGroup>
                                </StackItem>
                                {testResults?.pythonFlagWarnings && testResults.pythonFlagWarnings.length > 0 && (
                                    <StackItem>
                                        <Alert variant="info" isInline isPlain title={t('regex.composer.pythonFlagsWarning', { flags: testResults.pythonFlagWarnings.join(', ') })} />
                                    </StackItem>
                                )}
                                {testResults && !testResults.valid && (
                                    <StackItem>
                                        <Alert variant="danger" isInline isPlain title={t('regex.composer.invalidRegex')} data-testid="regex-composer-invalid-regex" />
                                    </StackItem>
                                )}
                                {testResults?.valid && testResults.results.length > 0 && (
                                    <StackItem>
                                        <div className="regex-composer-test-results" data-testid="regex-composer-test-results">
                                            {testResults.results.map((r, i) => (
                                                <div key={i} className={`regex-composer-test-result ${r.matches ? 'match' : 'no-match'}`}>
                                                    {r.matches ? <CheckCircleIcon /> : <TimesCircleIcon />}
                                                    <span className="regex-composer-test-result-text">
                                                        {r.matchedText !== null ? (
                                                            <>
                                                                {r.text.slice(0, r.matchIndex)}
                                                                <span className="regex-composer-test-result-matched">{r.matchedText}</span>
                                                                {r.text.slice(r.matchIndex + r.matchedText.length)}
                                                            </>
                                                        ) : r.text}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </StackItem>
                                )}
                            </Stack>
                        </div>
                    </Tab>
                </Tabs>
            </Modal>

            <Modal
                isOpen={isClearConfirmOpen}
                onClose={handleClearCancel}
                variant={ModalVariant.small}
                title={t('regex.composer.clearConfirmTitle')}
                actions={[
                    <Button key="confirm" variant="danger" onClick={handleClearConfirm} data-testid="regex-composer-clear-confirm">
                        {t('regex.composer.clear')}
                    </Button>,
                    <Button key="cancel" variant="tertiary" onClick={handleClearCancel} data-testid="regex-composer-clear-cancel">
                        {t('common.cancel')}
                    </Button>,
                ]}
            >
                {t('regex.composer.clearConfirmDescription')}
            </Modal>
        </>
    );
};

export default RegexComposerModal;
